// Error boundary for the Dashboards route.
// Modern.js renders this component when the page throws during render.
// The error + reset props are injected by the framework.
import { useAddI18nLocales, useTranslation } from './i18n';

/**
 * `useCentralizedMe()` (from `@crehana/auth`) THROWS on an expired/missing
 * session — see node_modules/@crehana/auth/dist/centralized/centralized.js:
 *   "The user must be logged in to use useCentralizedMe. Make sure you are
 *    waiting for the query to finish loading before calling this hook."
 *
 * The intended platform behaviour is that this throw bubbles all the way up to
 * the global `@crehana/layouts` boundary (RootErrorBoundaryWithSentry, mounted
 * by SPALayout in src/routes/layout.tsx), which renders the canonical
 * session-expired / error screen. That is exactly what `reports/tracking` shows,
 * because it has NO local route error boundary.
 *
 * This dashboards route DOES have a local boundary, so without special-casing it
 * would swallow the auth throw and render a raw message + retry button, leaving
 * the user on a broken screen. We re-throw the auth/session error so it reaches
 * the global boundary, mirroring tracking's behaviour. Re-throwing during render
 * propagates synchronously to the parent boundary — it renders its static
 * fallback once and does not re-loop.
 */
const isAuthSessionError = (error: Error): boolean =>
  /must be logged in|useCentralizedMe/i.test(error?.message ?? '');

export const DashboardsErrorBoundary = ({
  error,
  reset,
}: {
  error: Error;
  reset: () => void;
}) => {
  // Re-throw auth/session-expired errors so the global @crehana/layouts boundary
  // renders the canonical session-expired screen (same as reports/tracking).
  if (isAuthSessionError(error)) {
    throw error;
  }

  useAddI18nLocales();
  const { t } = useTranslation();

  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-cds-py-8 tw-cds-gap-5">
      <p className="tw-cds-font-body-02 tw-cds-text-secondary">
        {error.message}
      </p>
      <button
        type="button"
        className="tw-cds-font-label-01 tw-cds-text-brand"
        onClick={reset}
      >
        {t('ERROR_RETRY')}
      </button>
    </div>
  );
};
